import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { CurrencyInput } from "../components/ui/currency-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Plus, Trash2, Search, CreditCard, Printer } from "lucide-react";
import { SearchableSelect } from "../components/ui/searchable-select";



const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    supplier_id: "",
    date: new Date().toISOString().split("T")[0],
    amount: "",
    method: "cash",
    account_id: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [payRes, suppRes, cashRes, bankRes, ccRes] = await Promise.all([
        axios.get(`${API}/payments`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/cash-accounts`),
        axios.get(`${API}/bank-accounts`),
        axios.get(`${API}/credit-cards`),
      ]);
      setPayments(payRes.data);
      setSuppliers(suppRes.data);
      setCashAccounts(cashRes.data);
      setBankAccounts(bankRes.data);
      setCreditCards(ccRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplier_id || formData.amount <= 0) {
      toast.error("Tedarikçi ve tutar zorunludur");
      return;
    }

    try {
      await axios.post(`${API}/payments`, formData);
      toast.success("Ödeme kaydedildi");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to save:", error);
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu ödemeyi silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/payments/${id}`);
      toast.success("Ödeme silindi");
      fetchData();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: "",
      date: new Date().toISOString().split("T")[0],
      amount: "",
      method: "cash",
      account_id: "",
      notes: "",
    });
  };

  const getAccounts = () => {
    if (formData.method === "cash") return cashAccounts;
    if (formData.method === "bank") return bankAccounts;
    if (formData.method === "credit_card") return creditCards;
    return [];
  };

  const filteredPayments = payments.filter(
    (p) =>
      p.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.receipt_no.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const handlePagePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6 print:space-y-4" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Ödemeler
          </h1>
          <p className="text-slate-500 mt-1">Tedarikçilere ödeme kayıtları</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePagePrint} className="no-print">
            <Printer className="h-4 w-4 mr-2" /> Yazdır
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-payment-btn">
                <Plus className="h-5 w-5 mr-2" />
                Yeni Ödeme
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Yeni Ödeme</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Tedarikçi *</Label>
                <SearchableSelect
                  options={suppliers.map((s) => ({
                    value: s.id,
                    label: s.name,
                    subtitle: `Borç: ₺${s.balance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`,
                  }))}
                  value={formData.supplier_id}
                  onChange={(value) => setFormData({ ...formData, supplier_id: value })}
                  placeholder="Tedarikçi seçin"
                  searchPlaceholder="Tedarikçi ara..."
                  emptyMessage="Tedarikçi bulunamadı."
                  data-testid="pay-supplier-select"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tarih</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tutar *</Label>
                  <CurrencyInput
                    value={formData.amount}
                    onChange={(val) => setFormData({ ...formData, amount: val })}
                    placeholder="Tutar girin"
                    data-testid="pay-amount-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Ödeme Yöntemi</Label>
                <Select
                  value={formData.method}
                  onValueChange={(value) => setFormData({ ...formData, method: value, account_id: "" })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Nakit</SelectItem>
                    <SelectItem value="bank">Banka</SelectItem>
                    <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Hesap</Label>
                <Select
                  value={formData.account_id}
                  onValueChange={(value) => setFormData({ ...formData, account_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Hesap seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAccounts().map((acc) => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notlar</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek notlar"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  İptal
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="pay-save-btn">
                  Kaydet
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Ödeme ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0 md:p-0">
          {filteredPayments.length > 0 ? (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-slate-100">
                {filteredPayments.map((pay) => (
                  <div key={pay.id} className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-slate-900">{pay.supplier_name}</p>
                        <p className="text-xs font-mono text-slate-500">{pay.receipt_no}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(pay.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-slate-500">{pay.date}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        pay.method === "cash" ? "bg-emerald-100 text-emerald-700" :
                        pay.method === "bank" ? "bg-blue-100 text-blue-700" :
                        "bg-purple-100 text-purple-700"
                      }`}>
                        {pay.method === "cash" ? "Nakit" : pay.method === "bank" ? "Banka" : "Kredi Kartı"}
                      </span>
                    </div>
                    <div className="flex items-center justify-end">
                      <span className="font-semibold text-red-600 tabular-nums">
                        -₺{pay.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Makbuz No</TableHead>
                      <TableHead className="font-semibold">Tedarikçi</TableHead>
                      <TableHead className="font-semibold">Tarih</TableHead>
                      <TableHead className="font-semibold">Yöntem</TableHead>
                      <TableHead className="font-semibold text-right">Tutar</TableHead>
                      <TableHead className="font-semibold text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayments.map((pay) => (
                      <TableRow key={pay.id}>
                        <TableCell className="font-mono text-sm">{pay.receipt_no}</TableCell>
                        <TableCell className="font-medium">{pay.supplier_name}</TableCell>
                        <TableCell>{pay.date}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            pay.method === "cash" ? "bg-emerald-100 text-emerald-700" :
                            pay.method === "bank" ? "bg-blue-100 text-blue-700" :
                            "bg-purple-100 text-purple-700"
                          }`}>
                            {pay.method === "cash" ? "Nakit" : pay.method === "bank" ? "Banka" : "Kredi Kartı"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums text-red-600">
                          -₺{pay.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(pay.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <CreditCard className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{searchTerm ? "Arama sonucu bulunamadı" : "Henüz ödeme yok"}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Payments;
